'use client';

import React, { useState } from 'react';

import { HyperpayCheckoutForm } from './hyperpay-checkout-form';

interface HyperpaySuccessData {
  id?: string;
  resourcePath?: string;
  [key: string]: unknown;
}

export interface HyperpayCheckoutModalProps {
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  isOpen: boolean;
  onClose: () => void;
  checkoutId: string;
  integrity?: string;
  baseUrl?: string;
  onSuccess?: (data: HyperpaySuccessData) => void;
  onError?: (error: Error) => void;
  /** Checkout currency (ISO 4217) — drives the Apple Pay sheet config. */
  currency?: string;
  /** Merchant name shown on the Apple Pay sheet. */
  displayName?: string;
}

export function HyperpayCheckoutModal({
  contentClassName,
  contentStyle,
  isOpen,
  onClose,
  checkoutId,
  integrity,
  baseUrl,
  onSuccess,
  onError,
  currency,
  displayName,
}: HyperpayCheckoutModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSuccess = (data: HyperpaySuccessData) => {
    setIsProcessing(true);
    if (onSuccess) {
      onSuccess(data);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="bg-opacity-50 fixed inset-0 bg-black transition-opacity"
        onClick={!isProcessing ? onClose : undefined}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl bg-white shadow-xl ${contentClassName ?? ''}`}
          style={contentStyle}
        >
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Complete Payment
                </h2>
                {!isProcessing && (
                  <p className="mt-1 text-sm text-gray-500">
                    Enter your card details below. We accept all major cards
                    including Mada, Visa, Mastercard and American Express
                  </p>
                )}
              </div>
              {!isProcessing && (
                <button
                  onClick={onClose}
                  className="-mt-1 -mr-1 text-gray-400 transition-colors hover:text-gray-500"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            {isProcessing ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-gray-600"></div>
                <p className="text-gray-600">Processing your payment...</p>
                <p className="mt-2 text-sm text-gray-500">
                  Please do not close this window
                </p>
              </div>
            ) : (
              <>
                <HyperpayCheckoutForm
                  checkoutId={checkoutId}
                  integrity={integrity}
                  baseUrl={baseUrl}
                  onSuccess={handleSuccess}
                  onError={onError}
                  showApplePay={true}
                  currency={currency}
                  displayName={displayName}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
